import React from 'react';
import { MessageSquare, User, Building2 } from 'lucide-react';
import type { ProjectUpdate } from '@/types';

interface PartnerUpdatesFeedProps {
  updates: ProjectUpdate[];
  isLoading?: boolean;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Sanitize HTML content from updates
 * Converts basic HTML to readable text
 */
function sanitizeUpdateBody(body: string): string {
  // Remove HTML tags but preserve line breaks
  let text = body
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .trim();

  // Decode HTML entities
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  text = textArea.value;

  return text;
}

export function PartnerUpdatesFeed({ updates, isLoading }: PartnerUpdatesFeedProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-24" />
                <div className="h-3 bg-gray-100 rounded w-32 mt-1" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-100 rounded w-full" />
              <div className="h-3 bg-gray-100 rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (updates.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <MessageSquare className="mx-auto text-gray-300 mb-3" size={48} />
        <p className="text-gray-600 font-medium">No updates yet</p>
        <p className="text-gray-500 text-sm mt-1">
          Updates will appear here as the team works on your project.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {updates.map((update) => (
        <div
          key={update.id}
          className={`flex ${update.isPartnerNote ? 'justify-end' : 'justify-start'}`}
        >
          <div className={`max-w-[85%] ${update.isPartnerNote ? 'items-end' : 'items-start'} flex flex-col`}>
            {/* Sender name and timestamp */}
            <div className={`flex items-center gap-2 mb-1 px-1 ${update.isPartnerNote ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className="flex items-center gap-1.5">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    update.isPartnerNote
                      ? 'bg-blue-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}
                >
                  {update.isPartnerNote ? (
                    <Building2 size={12} />
                  ) : (
                    <User size={12} />
                  )}
                </div>
                <span className="font-medium text-gray-700 text-xs">
                  {update.createdBy.name}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {formatDateTime(update.createdAt)}
              </span>
            </div>

            {/* Message bubble */}
            <div
              className={`rounded-2xl px-4 py-2.5 shadow-sm ${
                update.isPartnerNote
                  ? 'bg-blue-500 text-white rounded-tr-sm'
                  : 'bg-green-500 text-white rounded-tl-sm'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap break-words">
                {sanitizeUpdateBody(update.body)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
